/* eslint-disable react-refresh/only-export-components */
import React from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import {
  LoadBrandingFlat,
  type Branding,
  API_BASE,
} from "@/services/api/branding-service";
import { tenantKeyFrom } from "@/tenant";
import { useLocation } from "react-router-dom";

/* ---------- small helpers ---------- */
function setFavicon(href?: string, appleTouchHref?: string) {
  const pairs: Array<[rel: string, url?: string]> = [
    ["icon", href],
    ["shortcut icon", href],
    ["apple-touch-icon", appleTouchHref || href],
  ];
  pairs.forEach(([rel, url]) => {
    let link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
    if (!link) {
      link = document.createElement("link");
      link.rel = rel;
      document.head.appendChild(link);
    }
    if (url) link.href = url;
  });
}

function setThemeColor(color?: string) {
  let meta = document.querySelector<HTMLMetaElement>(
    'meta[name="theme-color"]'
  );
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "theme-color";
    document.head.appendChild(meta);
  }
  meta.content = color || "#1976d2";
}

/** Inject/refresh the manifest link. Pass version to force refresh after branding loads. */
function ensureManifestLink(brandKey: string, version?: string) {
  const api = API_BASE.replace(/\/+$/, "");

  const params = new URLSearchParams();
  params.set("origin", location.origin);
  params.set("bp", (import.meta.env.VITE_ROUTER_BASENAME || "").trim());
  if (version) params.set("v", version);

  const href = `${api}/branding/manifest/${encodeURIComponent(
    brandKey
  )}.webmanifest?${params}`;

  let link = document.querySelector<HTMLLinkElement>('link[rel="manifest"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "manifest";
    document.head.appendChild(link);
  }
  if (link.href !== href) {
    link.type = "application/manifest+json";
    link.href = href;
  }
}

function setBodyBackground(bg?: string) {
  const root = document.documentElement;
  root.style.setProperty("--brand-bg", bg || "#ffffff");
  document.body.classList.add("brand-loaded");
}

/* ---------- simple error UIs ---------- */

const errorContainerStyle: React.CSSProperties = {
  padding: 24,
  maxWidth: 720,
  margin: "40px auto",
  fontFamily:
    "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  borderRadius: 8,
  background: "#fafafa",
  border: "1px solid #e0e0e0",
};

function MissingTenant() {
  return (
    <div style={errorContainerStyle}>
      <h2 style={{ marginBottom: 12 }}>No tenant in the URL</h2>
      <p style={{ marginBottom: 8 }}>
        This DigiAlert site needs a tenant code in the address to know which
        configuration to load.
      </p>
      <p style={{ marginBottom: 8 }}>
        Example:{" "}
        <code
          style={{ background: "#f5f5f5", padding: "2px 4px", borderRadius: 4 }}
        >
          /acme/login
        </code>{" "}
        or{" "}
        <code
          style={{ background: "#f5f5f5", padding: "2px 4px", borderRadius: 4 }}
        >
          /digicall/login
        </code>
        .
      </p>
      <p style={{ marginTop: 16 }}>
        If you reached this page from a link, please contact your administrator
        and ask them to confirm the correct DigiAlert link for your company.
      </p>
      <p>
        You can also contact <strong>Digicall IT support</strong> at{" "}
        <a href="mailto:PWASupport@digicallgroup.co.za">
          PWASupport@digicallgroup.co.za
        </a>
        .
      </p>
    </div>
  );
}

function UnknownTenant({ tenant }: { tenant?: string | null }) {
  return (
    <div style={errorContainerStyle}>
      <h2 style={{ marginBottom: 12 }}>We couldn’t find this tenant</h2>
      <p style={{ marginBottom: 8 }}>
        We don’t have any configuration for the tenant code{" "}
        <code
          style={{
            background: "#f5f5f5",
            padding: "2px 4px",
            borderRadius: 4,
          }}
        >
          {tenant || "(none)"}
        </code>
        .
      </p>

      <p style={{ marginBottom: 12 }}>
        This usually means one of the following:
      </p>
      <ul style={{ paddingLeft: 20, marginBottom: 12 }}>
        <li>
          The <strong>tenant code in the address bar</strong> is misspelled.
        </li>
        <li>
          The tenant <strong>has not been set up</strong> on this DigiAlert
          server yet.
        </li>
        <li>
          The tenant was <strong>removed or disabled</strong>.
        </li>
      </ul>

      <p style={{ marginBottom: 8 }}>
        Please check the link you used to open this page. If you’re not sure
        which tenant code to use:
      </p>
      <ul style={{ paddingLeft: 20 }}>
        <li>
          Ask your company administrator to confirm your{" "}
          <strong>DigiAlert link</strong>.
        </li>
        <li>
          You can also contact <strong>Digicall IT support</strong> at{" "}
          <a href="mailto:PWASupport@digicallgroup.co.za">
            PWASupport@digicallgroup.co.za
          </a>
          .
        </li>
      </ul>
    </div>
  );
}

/** Generic error when we can't load branding for *any* reason. */
function BrandingLoadError() {
  return (
    <div style={errorContainerStyle}>
      <h2 style={{ marginBottom: 12 }}>We couldn’t load the site</h2>
      <p style={{ marginBottom: 12 }}>
        The app couldn’t load the configuration for this tenant. This can happen
        for a few reasons:
      </p>
      <ul style={{ paddingLeft: 20, marginBottom: 12 }}>
        <li>
          The security certificate for this server is{" "}
          <strong>invalid or untrusted</strong>.
        </li>
        <li>
          There is a <strong>network connection problem</strong> between your
          device and the server.
        </li>
        <li>
          The <strong>tenant code in the address (for example /tenant)</strong>{" "}
          is not configured on the server or was removed.
        </li>
      </ul>
      <p style={{ marginBottom: 8 }}>
        Please try again in a few minutes. If the problem continues:
      </p>
      <ul style={{ paddingLeft: 20 }}>
        <li>
          Take note of any <strong>certificate / TLS warnings</strong> the
          browser shows when opening this page.
        </li>
        <li>
          Share the full address bar URL with your{" "}
          <strong>Digicall IT support</strong> so they can check that
          the tenant is correctly configured. You can also email{" "}
          <a href="mailto:PWASupport@digicallgroup.co.za">
            PWASupport@digicallgroup.co.za
          </a>
          .
        </li>
      </ul>
    </div>
  );
}

/* ---------- Context + Provider ---------- */
export const BrandingContext = React.createContext<Branding | null>(null);

type BrandingError = "missing" | "fatal" | "unknown" | null;

export default function BrandingProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [branding, setBranding] = React.useState<Branding | null>(null);
  const [error, setError] = React.useState<BrandingError>(null);

  const location = useLocation();
  const tenantKey = React.useMemo(
    () => tenantKeyFrom(location.pathname),
    [location.pathname]
  );

  React.useEffect(() => {
    const controller = new AbortController();

    (async () => {
      setBranding(null);
      setError(null);

      if (!tenantKey) {
        setError("missing");
        return;
      }

      // early manifest
      ensureManifestLink(tenantKey);

      try {
        const b = await LoadBrandingFlat(tenantKey, controller.signal);

        if (!b) {
          // probably aborted / transient – keep “Loading theme…”
          return;
        }

        setBranding(b);
        document.title = b.appName || "Digicall-Alert";
        setThemeColor(b.primaryColor);
        setFavicon(b.assets?.favicon, b.assets?.splash512 || b.assets?.icon192);
        setBodyBackground(b.backgroundColor);

        ensureManifestLink(tenantKey, b.version);
      } catch (err: any) {
        console.error("Failed to load branding", err);

        // 404 from API -> explicit "unknown tenant" UI
        if (err?.name === "UnknownTenant") {
          setError("unknown");
          return;
        }

        // Everything else (TLS / cert / DNS / 500 / etc.) -> generic fatal error
        setError("fatal");
      }
    })();

    return () => {
      controller.abort();
      document.body.classList.remove("brand-loaded");
    };
  }, [tenantKey]);

  const theme = React.useMemo(
    () =>
      createTheme({
        palette: {
          primary: { main: branding?.primaryColor || "#1976d2" },
          secondary: { main: branding?.secondaryColor || "#9c27b0" },
          background: { default: branding?.backgroundColor || "#ffffff" },
        },
      }),
    [branding]
  );

  if (error === "unknown") return <UnknownTenant tenant={tenantKey} />;
  if (error === "missing") return <MissingTenant />;
  if (error === "fatal") return <BrandingLoadError />;

  if (!branding) {
    // Tiny blocking loader so children only mount once branding exists
    return (
      <>
        <CssBaseline />
        <div style={{ padding: 24 }}>Loading theme…</div>
      </>
    );
  }

  return (
    <BrandingContext.Provider value={branding}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </BrandingContext.Provider>
  );
}
